import { FC } from 'react';

import { AppDashboard } from '@shared/types/app';
import Markdown from 'markdown-to-jsx';

import { useAnalytics } from '@hooks/useAnalytics';
import { useDynamicAction } from '@hooks/useDynamicAction';

import { useRouter } from '@router/index';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { SharedDashboardSectionProps } from '../DashboardScreen.types';

export const WeeklyMetricsAction: FC<{
  metric: AppDashboard['primaryTiles']['weeklyMetrics'][number];
}> = ({ metric }) => {
  const { track } = useAnalytics();
  const { isSubmittingDynamicAction, handleDynamicAction } = useDynamicAction();
  const {
    isSubmittingDynamicAction: isSubmittingCtaDynamicAction,
    handleDynamicAction: handleCtaDynamicAction,
  } = useDynamicAction();

  return (
    <div key={metric.title} data-testid={`Weekly Metric: ${metric.title}`} className="w-100 h-100">
      <Card indicator={metric.indicatorCount}>
        <div className="p-2">
          <div>
            <div className="d-flex justify-content-between">
              <div className="fs-4">
                <strong className="count">{metric.count}</strong>
              </div>
              <div>
                <IconButton
                  variant="smooth"
                  color="neutral"
                  disabled={isSubmittingDynamicAction}
                  onClick={() => {
                    track('Weekly Metric Arrow Clicked', metric);
                    handleDynamicAction(metric, { state: { back: '/', forceBack: true } });
                  }}
                >
                  <FontAwesomeIcon name="chevron-right" />
                </IconButton>
              </div>
            </div>
            <div style={{ fontSize: '.9rem', maxWidth: 110 }}>
              <strong className="metric-title">
                <Markdown options={{ forceInline: true }}>{metric.title}</Markdown>
              </strong>
            </div>
          </div>
          <div className="mt-2">
            {metric.currentUserCount ? (
              <Button
                color="teal"
                addBorder
                size="sm"
                startIcon={<strong>{metric.currentUserCount}</strong>}
                style={{ fontSize: '.7rem' }}
              >
                <span className="opacity-75">by you</span>
              </Button>
            ) : (
              <Button
                color="danger"
                variant="outlined dashed"
                startIcon={<FontAwesomeIcon name="plus" />}
                size="sm"
                style={{ fontSize: '.7rem' }}
                disabled={isSubmittingCtaDynamicAction}
                onClick={() => {
                  track('Weekly Metric CTA Clicked', metric);
                  handleCtaDynamicAction(metric.cta, { state: { back: '/', forceBack: true } });
                }}
              >
                {metric.cta.text}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export const WeeklyMetrics: FC<SharedDashboardSectionProps> = ({
  isAppDashboardReady,
  appDashboard,
}) => {
  if (!isAppDashboardReady) {
    return (
      <>
        <div>
          <div className="mb-2">
            <Skeleton height={22} />
          </div>
          <div className="mb-3">
            <Skeleton height={116} />
          </div>
        </div>
      </>
    );
  }

  if (!appDashboard?.primaryTiles) {
    return <></>;
  }

  const { title, weeklyMetrics } = appDashboard?.primaryTiles;

  return (
    <div className="w-100">
      <TitleToolbar text={title} size="sm" />
      <div className="d-flex gap-2">
        {weeklyMetrics.map((metric) => (
          <WeeklyMetricsAction key={metric.title} metric={metric} />
        ))}
      </div>
    </div>
  );
};
